import React from 'react';
import { Navigate } from 'react-router-dom';
import { colorBackgroundHomeHeader } from '@cloudscape-design/design-tokens';

import { Alert, Box, Button, Container, ContentLayout, Header, Link, NavigateLink, SpaceBetween } from 'components';

import { useAppSelector } from 'hooks';
import { goToUrl } from 'libs';
import { ROUTES } from 'routes';
import { useGithubAuthorizeMutation } from 'services/auth';
import { useGetUserDataQuery } from 'services/user';

import { Loading } from 'App/Loading';
import { selectAuthToken } from 'App/slice';

const GitHubIcon: React.FC = () => (
    <span>
        <svg viewBox="0 0 14 14" stroke="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M7 0.34375C6.125 0.34375 5.27083 0.510417 4.4375 0.84375C3.625 1.17708 2.90625 1.65625 2.28125 2.28125C1.65625 2.90625 1.17708 3.63542 0.84375 4.46875C0.510417 5.28125 0.34375 6.125 0.34375 7C0.34375 8.45833 0.760417 9.77083 1.59375 10.9375C2.44792 12.0833 3.55208 12.8854 4.90625 13.3438C5.05208 13.3646 5.15625 13.3333 5.21875 13.25C5.30208 13.1875 5.34375 13.1042 5.34375 13V11.875C4.57292 12.0417 3.96875 11.9375 3.53125 11.5625C3.30208 11.3958 3.15625 11.1979 3.09375 10.9688C3.01042 10.7396 2.89583 10.5417 2.75 10.375C2.66667 10.25 2.57292 10.1562 2.46875 10.0938L2.34375 10C2.17708 9.875 2.09375 9.78125 2.09375 9.71875C2.09375 9.65625 2.14583 9.625 2.25 9.625L2.40625 9.59375C2.67708 9.61458 2.92708 9.73958 3.15625 9.96875C3.28125 10.0729 3.36458 10.1771 3.40625 10.2812C3.67708 10.7188 4.03125 10.9583 4.46875 11C4.73958 11.0208 5.04167 10.9688 5.375 10.8438C5.41667 10.4479 5.55208 10.1458 5.78125 9.9375C4.86458 9.83333 4.17708 9.58333 3.71875 9.1875C3.07292 8.64583 2.75 7.80208 2.75 6.65625C2.75 5.96875 2.97917 5.375 3.4375 4.875C3.35417 4.6875 3.3125 4.47917 3.3125 4.25C3.27083 3.875 3.33333 3.48958 3.5 3.09375H3.6875C3.85417 3.09375 4.05208 3.13542 4.28125 3.21875C4.61458 3.34375 4.96875 3.53125 5.34375 3.78125C5.86458 3.63542 6.41667 3.5625 7 3.5625C7.58333 3.5625 8.13542 3.63542 8.65625 3.78125C9.19792 3.42708 9.66667 3.20833 10.0625 3.125C10.2708 3.08333 10.4167 3.07292 10.5 3.09375C10.6667 3.48958 10.7292 3.875 10.6875 4.25C10.6875 4.47917 10.6458 4.6875 10.5625 4.875C11.0208 5.375 11.25 5.96875 11.25 6.65625C11.25 7.82292 10.9271 8.66667 10.2812 9.1875C9.80208 9.58333 9.11458 9.83333 8.21875 9.9375C8.51042 10.2083 8.65625 10.625 8.65625 11.1875V13C8.65625 13.1042 8.6875 13.1875 8.75 13.25C8.83333 13.3333 8.95833 13.3646 9.125 13.3438C10.4583 12.8854 11.5417 12.0833 12.375 10.9375C13.2292 9.77083 13.6562 8.45833 13.6562 7C13.6562 6.125 13.4896 5.28125 13.1562 4.46875C12.8229 3.63542 12.3438 2.90625 11.7188 2.28125C11.0938 1.65625 10.3646 1.17708 9.53125 0.84375C8.71875 0.510417 7.875 0.34375 7 0.34375Z"
                fill="currentColor"
            />
        </svg>
    </span>
);

export const LoginByGithub: React.FC = () => {
    const token = useAppSelector(selectAuthToken);
    const localStorageIsAvailable = 'localStorage' in window;
    const {
        currentData: userData,
        error,
        isFetching,
    } = useGetUserDataQuery({ token }, { skip: !token || !localStorageIsAvailable });
    const [githubAuthorize, { isLoading, isError }] = useGithubAuthorizeMutation();

    const signInClick = () => {
        githubAuthorize()
            .unwrap()
            .then((data) => goToUrl(data.authorization_url))
            .catch(() => undefined);
    };

    if (token && localStorageIsAvailable && isFetching && !userData) return <Loading />;
    if (token && localStorageIsAvailable && userData?.username && !error) {
        return <Navigate replace to={ROUTES.RUNS.LIST} />;
    }

    return (
        <ContentLayout
            defaultPadding
            headerVariant="high-contrast"
            maxContentWidth={500}
            headerBackgroundStyle={colorBackgroundHomeHeader}
            header={
                <Box variant="h1" padding={{ vertical: 'xxxl' }} textAlign="center">
                    Welcome to dstack Sky
                </Box>
            }
        >
            <Container
                header={
                    <Box padding={{ bottom: 'xs' }}>
                        <Header variant="h2">Sign in</Header>
                    </Box>
                }
            >
                <SpaceBetween size="l">
                    {isError && <Alert type="error">Unable to start GitHub authentication</Alert>}
                    <Button
                        fullWidth
                        onClick={signInClick}
                        disabled={isLoading}
                        loading={isLoading}
                        variant="primary"
                        iconSvg={<GitHubIcon />}
                    >
                        Continue with GitHub
                    </Button>
                    <Box color="text-body-secondary" fontSize="body-s">
                        By continuing with GitHub, you agree to the{' '}
                        <Link href="https://dstack.ai/terms/" target="_blank" external>
                            Terms
                        </Link>{' '}
                        and{' '}
                        <Link href="https://dstack.ai/privacy/" target="_blank" external>
                            Privacy policy
                        </Link>
                    </Box>
                    <NavigateLink href={ROUTES.AUTH.TOKEN}>Sign in with a token</NavigateLink>
                </SpaceBetween>
            </Container>
        </ContentLayout>
    );
};
